/**
 * Configuration Service
 * 
 * Securely fetches application configuration from the backend API.
 * This eliminates the need for frontend environment variables and
 * ensures all sensitive configuration is managed server-side.
 */

class ConfigService {
  constructor() {
    this.config = null;
    this.loading = false;
    this.error = null;
  }

  /**
   * Fetch configuration from backend API
   * @returns {Promise<Object>} Application configuration
   */
  async getConfig() {
    // Return cached config if available
    if (this.config) {
      return this.config;
    }

    // Prevent multiple simultaneous requests
    if (this.loading) {
      return new Promise((resolve, reject) => {
        const checkLoading = () => {
          if (!this.loading) {
            if (this.config) {
              resolve(this.config);
            } else {
              reject(this.error);
            }
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    this.loading = true;
    this.error = null;

    try {
      // Determine API URL - prioritize HTTPS for Auth0 secure origin compliance
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      const apiUrls = isLocalhost ? [
        'http://localhost:3006',    // Local development primary
        'http://localhost:3005',    // Local development fallback
        window.location.origin,     // Same origin
        'https://105.30.59.48'      // VM backend HTTPS fallback
      ] : [
        window.location.origin,     // Same origin (nginx reverse proxy)
        'https://tms.ldm.co.za'     // Production domain fallback
      ];

      let response;
      let lastError;

      for (const apiUrl of apiUrls) {
        try {
          response = await fetch(`${apiUrl}/api/config`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            // Don't include credentials for public config endpoint
          });

          if (response.ok) {
            break; // Success, exit loop
          }
        } catch (error) {
          // eslint-disable-next-line no-unused-vars
          lastError = error;
          continue; // Try next URL
        }
      }

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch configuration: ${response?.status || 'Network error'}`);
      }

      const config = await response.json();
      
      // Validate required configuration
      this.validateConfig(config);
      
      this.config = config;
      this.loading = false;
      
      console.log('✅ Configuration loaded successfully');
      return this.config;

    } catch (error) {
      this.error = error;
      this.loading = false;
      console.error('❌ Failed to load configuration:', error);
      throw error;
    }
  }

  /**
   * Validate that required configuration is present
   * @param {Object} config - Configuration object to validate
   */
  validateConfig(config) {
    const required = [
      'auth0.domain',
      'auth0.clientId',
      'auth0.audience'
    ];

    for (const path of required) {
      const value = this.getNestedValue(config, path);
      if (!value) {
        throw new Error(`Missing required configuration: ${path}`);
      }
    }
  }

  /**
   * Get nested object value by dot notation path
   * @param {Object} obj - Object to search
   * @param {string} path - Dot notation path (e.g., 'auth0.domain')
   * @returns {*} Value at path or undefined
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Get Auth0 configuration
   * @returns {Promise<Object>} Auth0 configuration
   */
  async getAuth0Config() {
    const config = await this.getConfig();
    return config.auth0;
  }

  /**
   * Get API configuration
   * @returns {Promise<Object>} API configuration
   */
  async getApiConfig() {
    const config = await this.getConfig();
    return config.api;
  }

  /**
   * Clear cached configuration (useful for testing or config updates)
   */
  clearCache() {
    this.config = null;
    this.loading = false;
    this.error = null;
  }

  /**
   * Check if configuration is loaded
   * @returns {boolean} True if config is loaded
   */
  isLoaded() {
    return this.config !== null;
  }

  /**
   * Get loading state
   * @returns {boolean} True if currently loading
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Get error state
   * @returns {Error|null} Current error or null
   */
  getError() {
    return this.error;
  }
}

// Export singleton instance
const configService = new ConfigService();
export default configService;

// Named exports for convenience
export const getConfig = () => configService.getConfig();
export const getAuth0Config = () => configService.getAuth0Config();
export const getApiConfig = () => configService.getApiConfig();
